import React, { useEffect, useState } from "react";
import AlertComponent from "components/Alert";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { ApprovalAction, ApprovalTaskStatues } from "common";
import { useGetProfileQuery } from "redux/services/membership";
import { useApproverUpdateDisbursementDateMutation, useUpdateApprovalTaskMutation } from "redux/services/approvalProcessor";
import { dateFormat, FORMATS, getOrganisationMembership } from "utils";
import { useRoles } from "hooks/useRoles";
import { ApproverUpdateDisbursementDateRequest } from "redux/services/approvalProcessor/types";

interface Props {
  isOpen: boolean;
  type?: ApprovalTaskStatues;
  approvalTask?: any;
  selectedDisbursementDate?: Date;
  onClose: () => void;
  onSuccess?: () => void;
}

const ProcessDeclineDialog = ({
  isOpen,
  type,
  approvalTask,
  selectedDisbursementDate,
  onClose,
  onSuccess,
}: Props) => {
  const toast = useToast();
  const cancelRef = React.useRef(null);
  const [commentValue, setCommentValue] = useState<string>();
  const [updateApprovalTask, { isLoading, isSuccess, isError, error }] =
    useUpdateApprovalTaskMutation();

  const isNewApproverError = isError && error && (error as any)?.data?.errors?.some(
    (err: any) => err.code === "017.S01.404.03"
  );

  const { data: ProfileData } = useGetProfileQuery();
  const membershipOrganisation = getOrganisationMembership(
    ProfileData?.data?.memberships
  );

  const [
    approverUpdateDisbursementDate,
    {
      isLoading: isUpdateDisbursementDateLoading,
    }
  ] = useApproverUpdateDisbursementDateMutation();

  const { isPaymentOpsRole, isInhouseApproverRole, isPSPApproverRole, isInHouseCheckerRole, isPSPCheckerRole } = useRoles();

  const renderTitleAction = () => {
    switch (true) {
      case isPaymentOpsRole && type === ApprovalTaskStatues.APPROVED:
        return "Process";
      case isPaymentOpsRole && type === ApprovalTaskStatues.REJECTED:
        return "Decline";
      case (isInHouseCheckerRole || isPSPCheckerRole) && type === ApprovalTaskStatues.APPROVED:
        return "Proceed";
      case (isInhouseApproverRole || isPSPApproverRole) && type === ApprovalTaskStatues.APPROVED:
        return "Approve";
      case (isInHouseCheckerRole || isPSPCheckerRole || isInhouseApproverRole || isPSPApproverRole) &&
        type === ApprovalTaskStatues.REJECTED:
        return "Reject";
      default:
        return "";
    }
  };

  const handleSubmit = async () => {
    try {
      if ((isInhouseApproverRole || isPSPApproverRole) && selectedDisbursementDate) {
        const bodyUpdateDisbursementDate: ApproverUpdateDisbursementDateRequest = {
          orgToken: membershipOrganisation?.token ?? "",
          processId: approvalTask?.id ?? "",
          processVariables: {
            overallInformation: {
              disbursementDate: dateFormat(
                selectedDisbursementDate?.toString(),
                false,
                FORMATS["YYYY/MM/DD"]
              ) ?? ""
            },
          },
        };

        await approverUpdateDisbursementDate(bodyUpdateDisbursementDate).unwrap()
      }

      const body = {
        orgToken: membershipOrganisation?.token,
        id: approvalTask?.referenceNumber,
        status: type,
        actorId: ProfileData?.data?.userId,
        comment: commentValue,
      };

      await updateApprovalTask(body).unwrap();
    } catch (error: any) {
      const isShowToastError = error?.data?.errors[0]?.code !== "017.S01.404.03";

      isShowToastError &&
        toast({
          title: error?.data?.errors[0]?.message ?? "Something went wrong",
          description: "",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
    }
  };

  useEffect(() => {
    if (isSuccess) {
      let actionTitle;
      switch (true) {
        case isPaymentOpsRole && type === ApprovalTaskStatues.APPROVED:
          actionTitle = ApprovalAction.PROCESSED;
          break;
        case isPaymentOpsRole && type === ApprovalTaskStatues.REJECTED:
          actionTitle = ApprovalAction.DECLINED;
          break;
        case (isInHouseCheckerRole || isPSPCheckerRole) && type === ApprovalTaskStatues.APPROVED:
          actionTitle = ApprovalAction.APPROVED;
          break;
        case (isInHouseCheckerRole || isPSPCheckerRole) && type === ApprovalTaskStatues.REJECTED:
          actionTitle = ApprovalAction.REJECTED;
          break;
        case (isInhouseApproverRole || isPSPApproverRole) && type === ApprovalTaskStatues.APPROVED:
          actionTitle = ApprovalAction.APPROVED;
          break;
        case (isInhouseApproverRole || isPSPApproverRole) && type === ApprovalTaskStatues.REJECTED:
          actionTitle = ApprovalAction.REJECTED;
          break;
        default:
          actionTitle = "";
          break;
      }

      onSuccess?.();
      onClose?.();

      toast({
        title: `Payment successfully ${actionTitle}.`,
        description: "",
        status: type === ApprovalTaskStatues.APPROVED ? "success" : "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useEffect(() => {
    if (isOpen) setCommentValue("");
  }, [isOpen]);

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={isLoading ? () => { } : onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {!isNewApproverError ? `${renderTitleAction()} Payment` : "Warning"}
          </AlertDialogHeader>

          {!isNewApproverError && (
            <AlertDialogBody>
              {isError && (
                <AlertComponent
                  data-testid="alert-error"
                  status="error"
                  mb="10px"
                  title={
                    (error as any)?.data?.errors
                      ?.map((err: any) => err.message)
                      ?.join(", ") ?? "Something went wrong!"
                  }
                />
              )}

              {commentValue && commentValue.length >= 250 && (
                <AlertComponent
                  data-testid="alert-error"
                  status="error"
                  mb="10px"
                  title="Comment must be less than 250 characters"
                />
              )}

              <Text fontSize="14px" lineHeight="1.5">
                Are you sure you want to{` `}
                {renderTitleAction().toLowerCase()}
                {` `}this payment? This cannot be undone.
              </Text>

              <Textarea
                data-testid="decline-reason-input"
                mt="16px"
                fontSize="14px"
                placeholder="Add a comment"
                value={commentValue}
                onChange={(e) => setCommentValue(e.target.value)}
                isInvalid={!!(commentValue && commentValue?.length >= 250)}
              />
            </AlertDialogBody>
          )}

          {isNewApproverError && (
            <AlertDialogBody>
              <Text fontSize="14px" lineHeight="1.5">
                Approval/Rejection is not allowed for this payroll record, as it was created before your account was onboarded.
              </Text>
            </AlertDialogBody>
          )}

          {!isNewApproverError && (
            <AlertDialogFooter>
              <Button data-testid="cancel-button" ref={cancelRef} onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                data-testid="decline-button"
                colorScheme="blue"
                onClick={handleSubmit}
                ml={3}
                disabled={
                  isUpdateDisbursementDateLoading || isLoading ||
                  (type === ApprovalTaskStatues.REJECTED && !commentValue) ||
                  !!(
                    (type === ApprovalTaskStatues.APPROVED ||
                      type === ApprovalTaskStatues.REJECTED) &&
                    commentValue &&
                    commentValue?.length >= 250
                  )
                }
                isLoading={isLoading || isUpdateDisbursementDateLoading}
              >
                Confirm
              </Button>
            </AlertDialogFooter>
          )}

          {isNewApproverError && (
            <AlertDialogFooter>
              <Button
                colorScheme="blue"
                onClick={onClose}
                ml={3}
              >
                OK
              </Button>
            </AlertDialogFooter>
          )}

        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default ProcessDeclineDialog;
